import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import styles from './Summary.module.scss'

import PeripheralBridge from '../../utils/peripheralBridge'
import useGetSummaryReport from '../../hooks/useGetSummaryReport'
import moment from 'moment'
import { map, startCase, isEqual, set } from 'lodash'
import { centsToDollars } from '../../utils/formatters'
import { orderSummaryKeys } from "../../../src/utils/orderUtils"
import { getNetworkAvailableValue, networkSignalSpeed } from '../../utils/networkConnected'
import SidebarModal from '../SidebarModal'

import { startSyncCycle } from '../../hooks/useSafMode'

import { APPLICATION_STRINGS } from '../../strings'
import { getDeviceMode } from '../../VNMode/Selectors'
import { useSelector } from 'react-redux'
import ConfirmModalV2 from '../ConfirmModalV2'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'

const OrderSummary = ({
  attendantName,
  allRevenueInCents,
  deviceUuid,
  className,
  menuName,
  isPaxDevice,
  offlineCardIds,
  offlineLocalIds,
  onCheckNetworkClick,
  cardReaderFailedCardCount,
  cardReaderDeletedCardCount,
  cardReaderTotalCardCount,
  cardReaderUploadedCardCount,
  unsyncableIds,
  onClose,
  onRequestSyncRefund,
  unsyncedRefundIds,
  venueName,
  safResponse,
  safIsUploading,
  setSafIsUploading,
  safUploadComplete,
}) => {
  const classNames = cn(styles.orderSummary, className)

  const offlineOrders = offlineLocalIds.length
  const offlineCards = offlineCardIds.length
  const unsyncableOrders = unsyncableIds.length
  const [getSummaryReport, { data: totals }] = useGetSummaryReport()
  const isMobileViewPort = useGetIsMobileScreen()

  // TODO(mkramerl): Improve card reader disconnected error handling.
  const [showCardReaderConnectError, setShowCardReaderConnectError] = useState(false)
  const [syncRequestMade, setSyncRequestMade] = useState(false)
  const [stopSyncRequestMade, setStopSyncRequestMade] = useState(false)
  const [shouldSync, setShouldSync] = useState(false)
  const [signalSpeed, setSignalSpeed] = useState(4);
  const [insufficientNetwork, setInsufficientNetwork] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const cfdMode = useSelector(state => getDeviceMode(state))

  const onContinueSyncClick = () => {
    setSyncRequestMade(false)
    setShouldSync(true)
  }

  const onSyncOrdersClick = () => {
    setSyncRequestMade(true)

    if (!getNetworkAvailableValue()) setInsufficientNetwork(true)
  }

  const onStopSyncClick = () => {
    setShouldSync(false)

    setStopSyncRequestMade(false)
    setSyncRequestMade(false)
    setSafIsUploading(false)
  }

  const syncRefunds = () => {
    unsyncedRefundIds.forEach(onRequestSyncRefund)
  }

  useEffect(() => {
    getSummaryReport()
    try {
      networkSignalSpeed(setSignalSpeed)
    } catch {
      setSignalSpeed(0)
    }
  }, [])

  let remainingOfflineCards = offlineCards
  useEffect(() => {
    // If the sync is complete and not currently uploading, and there are no offline cards,
    // show the completion modal.
    if (safUploadComplete && !safIsUploading && (remainingOfflineCards === 0)) {
      setShowCompletionModal(true)
    }
  }, [safIsUploading])

  useEffect(() => {
    if (!shouldSync) {
      return
    }

    const bridgeUnavailableCallback = () => {
      console.log('Unable to connect to peripheral. Please try again later.')
      setShowCardReaderConnectError(true)
      onStopSyncClick()
      return
    }

    const bridgeInstance = PeripheralBridge.getBridge()
    startSyncCycle(false, bridgeUnavailableCallback, bridgeInstance, cfdMode)

    setShouldSync(false)
  }, [shouldSync])

  const renderFieldRow = (fields, index) => (
    <div
      className={styles.row}
      key={`${fields?.columnOne?.value}-${index}`}
    >
      {map(fields, (field) => (
        <div className={styles.column} style={field?.style}>
          {field?.value}
        </div>
      ))}
    </div>
  )

  const createField = (value = '', style = {}) => ({
    value,
    style,
  })

  const addWhiteTextStyle = {
    color: 'white',
  }

  const addBoldWhiteText = {
    color: 'white',
    fontWeight: '700',
  }

  const sectionOneRows = [
    [
      createField('Venue: ' + venueName),
    ],
    [
      createField('Menu: ' + menuName),
    ],
    [
      createField('Date: ' + moment().format('MM/DD/YY HH:mm A')),
    ],
    [
      createField('Attendant: ' + attendantName),
    ],
    [
      createField('Device: ' + deviceUuid),
    ],
  ]

  let sectionTwoRows = [
    [
      createField('Device Sync Status', addBoldWhiteText),
      createField('Qty', addBoldWhiteText)
    ]
  ]

  // If the ttotal card count is negative, it is an indicator that it isn't supplied by the reader
  // yet and we should use the internal offline card count instead. See the peripheral selector for
  // more details.
  if (isPaxDevice && cardReaderTotalCardCount >= 0) {
    // For PAX devices, the remaining offline cards is the total card count minus the uploaded card
    // count.
    remainingOfflineCards = (cardReaderUploadedCardCount > 0) ? (cardReaderTotalCardCount - cardReaderUploadedCardCount) : cardReaderTotalCardCount
    remainingOfflineCards = (remainingOfflineCards <= cardReaderFailedCardCount) ? 0 : remainingOfflineCards
  }

  if (isPaxDevice) {
    sectionTwoRows.push(
      [
        createField('Offline Card Transactions', remainingOfflineCards > 0 ? {color: '#EB628D'} : {}),
        createField((remainingOfflineCards >= 0) ? remainingOfflineCards : offlineCards, {color: remainingOfflineCards > 0 ? '#EB628D' : 'white'})
      ],
      // [
      //   createField('Uploaded Card Transactions', cardReaderUploadedCardCount > 0 ? {color: '#EB628D'} : {}),
      //   createField((cardReaderUploadedCardCount >= 0) ? cardReaderUploadedCardCount : '-', {color: cardReaderUploadedCardCount > 0 ? '#EB628D' : 'white'})
      // ],
      // [
      //   createField('Failed Card Transactions', cardReaderFailedCardCount > 0 ? {color: '#EB628D'} : {}),
      //   createField((cardReaderFailedCardCount >= 0) ? cardReaderFailedCardCount : 0, {color: cardReaderFailedCardCount > 0 ? '#EB628D' : 'white'})
      // ],
      // [
      //   createField('Deleted Card Transactions', cardReaderDeletedCardCount > 0 ? {color: '#EB628D'} : {}),
      //   createField((cardReaderDeletedCardCount >= 0) ? cardReaderDeletedCardCount : '-', {color: cardReaderDeletedCardCount > 0 ? '#EB628D' : 'white'})
      // ]
    )
  } else {
    sectionTwoRows.push(
      [
        createField('Unsynced Card Transactions', offlineCards > 0 ? {color: '#EB628D'} : {}),
        createField(offlineCards, {color: offlineCards > 0 ? '#EB628D' : '#999999'})
      ]
    )
  }

  sectionTwoRows.push(
    [
      createField("Unsynced Order Details Data", offlineOrders > 0 ? {color: '#EB628D'} : {}),
      createField(offlineOrders ?? '0', {color: offlineOrders > 0 ? '#EB628D' : '#999999'})
    ],
    [
      createField('Unsynced Refund Requests', unsyncedRefundIds.length > 0 ? {color: '#EB628D'} : {}),
      createField(unsyncedRefundIds.length ?? '0', {color: unsyncedRefundIds.length > 0 ? '#EB628D' : '#999999'})
    ],
    [
      createField('Defective Orders Unable to Sync', unsyncableOrders > 0 ? {color: '#EB628D'} : {}),
      createField(unsyncableOrders ?? '0', {color: unsyncableOrders > 0 ? '#EB628D' : '#999999'})
    ]
  )

  const sectionThreeRows = [
    [
      createField("Today’s Synced Orders Totals", addBoldWhiteText),
      createField('Amount', addBoldWhiteText)
    ],
    [
      createField('Gross Sales'),
      createField(totals?.gross ?? centsToDollars(allRevenueInCents), addWhiteTextStyle),
    ],
    [
      createField('Discounts'),
      createField(totals?.discounts ?? '-', addWhiteTextStyle)
    ],
    [
      createField('Refunds'),
      createField(totals?.refunds ?? '-', addWhiteTextStyle)
    ],
    [
      createField('Net Sales'),
      createField(totals?.net ?? centsToDollars(allRevenueInCents), addWhiteTextStyle),
    ],
    [
      createField('Tax'),
      createField(totals?.tax ?? '_', addWhiteTextStyle),
    ],
    [
      createField('Tips'),
      createField(totals?.tips ?? '-', addWhiteTextStyle)
    ],

  ]

  const sectionFourDefaults = [
    [
      createField('Today’s Synced Orders Tenders', addBoldWhiteText),
      createField('Amount', addBoldWhiteText)
    ],
    [
      createField('Cash'),
      createField(totals?.cash ?? '$0.00', addWhiteTextStyle),
    ],
    [
      createField('Card'),
      createField(totals?.creditCard ?? '$0.00', addWhiteTextStyle)
    ],
    [
      createField('Mobile Wallet'),
      createField(totals?.virtualCurrency ?? '$0.00', addWhiteTextStyle)
    ]
  ]
 
  const syncMessage = isMobileViewPort
    ? "If you continue, you will be blocked\n from any further actions on this\n device until the sync is complete.\n This process can take several\n minutes."
    : "If you continue, you will be blocked\n from any further actions on this device\n until the sync is complete. This\n process can take several minutes."

  const stopSyncMessage = isMobileViewPort
    ? "Stopping the sync will leave some orders unsynced."
    : "Stopping the sync will leave some\n orders unsynced."


  const noSyncMessage = 'Nothing to sync'

  const insufficientNetworkMessage = "Your network connection is\n insufficient. Please find a stable\n network connection and retry syncing\n orders."

  const sectionFourRows = (() => {
    if (!totals || isEqual(Object.keys(totals), orderSummaryKeys)) {
      return sectionFourDefaults;
    }
    for (const key in totals) {
      if (orderSummaryKeys.includes(key)) {
        continue;
      }
      let rowEl = [
        createField(startCase(key)),
        createField(totals[key] ?? '-', addWhiteTextStyle)
      ];
      sectionFourDefaults.push(rowEl)
    }
    return sectionFourDefaults
  })()

  const sections = [sectionOneRows, sectionTwoRows, sectionThreeRows, sectionFourRows]
  const content = map(sections, (section) => {
    return (
      <>
        {map(section, renderFieldRow)}
        <div className={styles.borderRow}></div>

      </>
    )
  })

  return (
    <>
    <SidebarModal
      show
      title="Device Summary"
      onClose={onClose}
    >
      <div className={classNames}>
        <div className={styles.scrollable}>
          {!safResponse?.success && (offlineCards > 0 || offlineOrders > 0) &&
            (
              <div className={styles.syncDetailsContainer}>
                <div>
                  <div>{APPLICATION_STRINGS.SAF_ERROR}</div>
                </div>
              </div>
            )
          }

          {content}
        </div>

        {!safIsUploading ? (
          <div className={styles.syncButtonContainer}>
            <button className={styles.syncButton} onClick={syncRefunds}>
              Sync Refunds
            </button>
            <button className={cn([styles.syncButton, styles.shift4blue])} onClick={() => onSyncOrdersClick()}>
              {'Sync Orders'}
            </button>
          </div>
        ) :
          <div className={styles.syncButtonContainer}>
            <button className={styles.stopSyncButton} onClick={() => setStopSyncRequestMade(true)}>
              Stop Syncing
            </button>
          </div>
        }
      </div>
    </SidebarModal>

    {/* The below Confirm Dialogs need to be outside of the SidebarModal component */}
    {syncRequestMade && !insufficientNetwork && (remainingOfflineCards > 0 || offlineOrders > 0) && (
      <ConfirmModalV2
        onButtonOneClick={() => setSyncRequestMade(false)}
        onButtonTwoClick={onContinueSyncClick}
        headerText={''}
        subtext={syncMessage}
        buttonOneText={'Cancel Sync'}
        buttonTwoText={'Continue'}
      />
    )}
    {syncRequestMade && insufficientNetwork && (
      <ConfirmModalV2
        onButtonOneClick={() => setSyncRequestMade(false)}
        onButtonTwoClick={onCheckNetworkClick}
        headerText={''}
        subtext={insufficientNetworkMessage}
        buttonOneText={'Cancel Sync'}
        buttonTwoText={'Check Network'}
      />
    )}
    {
      stopSyncRequestMade && (
        <ConfirmModalV2
          onButtonOneClick={() => onStopSyncClick()}
          onButtonTwoClick={() => setStopSyncRequestMade(false)}
          headerText={''}
          subtext={stopSyncMessage}
          buttonOneText={'Stop Sync Now'}
          buttonTwoText={'Continue Sync'}
        />
      )
    }
    {
      syncRequestMade && remainingOfflineCards === 0 && offlineOrders === 0 && (
        <ConfirmModalV2
          onButtonTwoClick={() => setSyncRequestMade(false)}
          headerText={''}
          subtext={noSyncMessage}
          buttonTwoText={'OK'}
          singleButtonOnly={true}
        />
      )
    }
    {
      showCompletionModal && (
        <ConfirmModalV2
          onButtonTwoClick={() => setShowCompletionModal(false)}
          headerText={''}
          subtext={'All orders are now synced.'}
          buttonTwoText={'OK'}
          singleButtonOnly={true}
        />
      )
    }
    {
      showCardReaderConnectError && (
        <ConfirmModalV2
          onButtonTwoClick={() => setShowCardReaderConnectError(false)}
          headerText={''}
          subtext="No card reader connection. Try syncing again."
          buttonTwoText={'OK'}
          singleButtonOnly={true}
        />
      )
    }
    </>
  )
}

OrderSummary.defaultProps = {
  allRevenueInCents: 0,
  menuName: '',
  offlineCardIds: [],
  offlineLocalIds: [],
  isPaxDevice: true,
  cardReaderFailedCardCount: -1,
  cardReaderDeletedCardCount: -1,
  cardReaderTotalCardCount: -1,
  cardReaderUploadedCardCount: -1,
  unsyncableIds: [],
  orderCounts: {},
}

OrderSummary.propTypes = {
  allRevenueInCents: PropTypes.number,
  menuName: PropTypes.string,
  offlineCardIds: PropTypes.array,
  offlineLocalIds: PropTypes.array,
  onCheckNetworkClick: PropTypes.func,
  isPaxDevice: PropTypes.bool,
  cardReaderFailedCardCount: PropTypes.number,
  cardReaderDeletedCardCount: PropTypes.number,
  cardReaderTotalCardCount: PropTypes.number,
  cardReaderUploadedCardCount: PropTypes.number,
  unsyncableIds: PropTypes.array,
  onInitializeSync: PropTypes.func,
  orderCounts: PropTypes.object,
  updateSafIsUploading: PropTypes.func,
}

export default OrderSummary
